/* eslint relay/must-colocate-fragment-spreads:0 */
import {format, parseISO} from 'date-fns'
import {For} from 'react-loops'
import {graphql, useFragment} from 'react-relay/hooks'
import {Box, Flex, Grid, HStack, Show, Stack, useTheme} from '@chakra-ui/react'
import {
  faCalendarDay as farCalendarDay,
  faMapMarkerAlt as farMapMarkerAlt,
} from '@fortawesome/pro-regular-svg-icons'

import {Header_user$key} from './__generated__/Header_user.graphql'
import cover from './cover.png'

import {Avatar} from 'components/AvatarNew'
import {Heading} from 'components/Heading'
import {FontAwesomeIcon} from 'components/FontAwesomeIcon'
import {LocalIcon} from 'components/LocalIcon'

interface HeaderProps {
  user: Header_user$key
}

export function Header(props: HeaderProps) {
  const theme = useTheme()
  const user = useFragment(
    graphql`
      fragment Header_user on User {
        ...Avatar_actor @arguments(size: 116)
        createdAt
        birthDayPrivacy
        displayName
        profile {
          age
        }
        city {
          name
          province {
            shortName
          }
        }
        stats {
          gamesPlayed
          sport {
            slug
          }
        }
      }
    `,
    props.user
  )
  const ageMarkup = user.birthDayPrivacy ? (
    <Box color="whiteAlpha.700">{`(${user.profile.age}) yrs`}</Box>
  ) : null

  return (
    <Flex
      align="center"
      backgroundSize="cover"
      minH={[32, 200]}
      p={4}
      position="relative"
      style={{backgroundImage: `url('${cover}')`}}
    >
      <Flex align="center" justify="center">
        <Avatar
          actor={user}
          border="4px solid"
          borderColor="white"
          borderRadius="full"
          boxShadow="md"
          boxSizing="content-box"
          h={[24, 116]}
          mr={[2, 4]}
          w={[24, 116]}
        />
        <Box>
          <HStack>
            <Heading
              as="h1"
              color="white"
              fontSize={[`lg`, `2xl`]}
              fontWeight="medium"
            >
              {user.displayName}
            </Heading>
            {ageMarkup}
          </HStack>
          <Stack mt={4} spacing={1}>
            {user.city && (
              <Box color="white" fontSize="sm" fontWeight="normal">
                <FontAwesomeIcon icon={farMapMarkerAlt} />
                {` `}
                {user.city.name}, {user.city.province.shortName}
              </Box>
            )}
            <Box color="white" fontSize="sm" fontWeight="normal">
              <FontAwesomeIcon icon={farCalendarDay} />
              {` `}
              Joined {format(parseISO(user.createdAt), `MMM dd, yyyy`)}
            </Box>
          </Stack>
        </Box>
      </Flex>

      <Show above="md">
        <Grid gap={4} ml="auto" templateColumns="1fr 1fr">
          <For
            of={user.stats}
            as={(stat) => (
              <Flex
                align="center"
                bg="white"
                borderRadius="md"
                boxShadow="md"
                p={2}
              >
                <Flex
                  align="center"
                  bg="green.50"
                  borderRadius="md"
                  h={12}
                  justify="center"
                  w={12}
                >
                  <LocalIcon
                    fill={theme.colors.green[500]}
                    icon={stat.sport.slug}
                    size={32}
                  />
                </Flex>

                <Box lineHeight="none" ml={2}>
                  <Box color="gray.700" fontSize="2xl" fontWeight="bold">
                    {stat.gamesPlayed}
                  </Box>
                  <Box
                    color="gray.500"
                    fontSize="sm"
                    fontWeight="medium"
                    mt={1}
                  >
                    Played
                  </Box>
                </Box>
              </Flex>
            )}
          />
        </Grid>
      </Show>
    </Flex>
  )
}
